/**
 * Zod input schemas: paypal. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
// ── designboom PayPal (Phase 1, Sep 2026) ──────────────────────────────────
export const PaypalIpnMarkInvoicedSchema = z.object({
    txnId: z.string().trim().min(1).max(64),
    sent: z.boolean(),
});
/** Bind one of the stored fuzzy candidates as the record's account. */
export const PaypalIpnConfirmAccountSchema = z.object({
    txnId: z.string().trim().min(1).max(64),
    accountId: z.string().trim().min(15).max(18),
});
/** Create the SFDC Account (+ billing Contact) for a PayPal record with no
 *  match — the confirm popup's edited fields (Sep 2026). */
export const PaypalIpnCreateAccountSchema = z.object({
    txnId: z.string().trim().min(1).max(64),
    name: z.string().trim().min(2).max(255),
    type: z.string().trim().min(1).max(60),
    industry: z.string().trim().max(60).nullish(),
    website: z.string().trim().max(255).nullish(),
    billingStreet: z.string().trim().max(255).nullish(),
    billingCity: z.string().trim().max(80).nullish(),
    billingZip: z.string().trim().max(20).nullish(),
    billingState: z.string().trim().max(80).nullish(),
    /** ISO-2 country code (the address picklists are code-driven). */
    billingCountryCode: z.string().trim().length(2).nullish(),
    vat: z.string().trim().max(60).nullish(),
    contactFirstName: z.string().trim().max(80).nullish(),
    contactLastName: z.string().trim().min(1).max(80),
    contactEmail: z.string().trim().toLowerCase().email().max(120),
    /** Retry after a duplicate warning: create anyway. */
    allowDuplicate: z.boolean().nullish(),
});
/** Run / resume the SFDC order chain for one PayPal record. */
export const PaypalIpnCreateOrderSchema = z.object({
    txnId: z.string().trim().min(1).max(64),
    /** Forget the stored chain and start from the opportunity again (after the
     *  half-built records were deleted in Salesforce). */
    restart: z.boolean().nullish(),
});
/** Monthly statement email config (recipients + send day). */
export const PaypalStatementConfigSchema = z.object({
    recipients: z.array(z.string().trim().toLowerCase().email()).min(1).max(10),
    sendDay: z.number().int().min(1).max(28),
    enabled: z.boolean(),
    newRecordRecipients: z.array(z.string().trim().toLowerCase().email()).max(10).nullish(),
});
/** Ad-hoc statement send over a custom (inclusive) date range. */
export const PaypalStatementSendSchema = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Send only to the caller (testing) instead of the configured recipients. */
    testToSelf: z.boolean().optional(),
    /** Which date puts a payment in the range: its payment date (default) or its SAP invoice's issue date. */
    basis: z.enum(['payment', 'invoice']).optional(),
}).refine((v) => v.to >= v.from, { message: 'to must not be before from' });
/** Product-name matching table rows (item + optional exact amount → name). */
/** Save the year's per-category revenue goals (USD). */
export const PaypalRevenueGoalsSetSchema = z.object({
    year: z.number().int().min(2020).max(2100),
    goals: z.record(z.string().trim().min(1).max(60), z.number().min(0).max(1e9)),
    currency: z.enum(['CHF', 'USD']).nullish(),
});
export const PaypalProductNamesSetSchema = z.object({
    rows: z.array(z.object({
        item: z.string().trim().min(1).max(300),
        amount: z.number().positive().nullable().optional(),
        name: z.string().trim().min(1).max(120),
    })).max(200),
});
/** Create + send the customer invoice for one PayPal transaction. */
export const PaypalCreateInvoiceSchema = z.object({
    txnId: z.string().trim().min(5).max(64),
});
/** Signed URL for a stored invoice PDF (view or download). */
export const PaypalInvoiceUrlSchema = z.object({
    txnId: z.string().trim().min(5).max(64),
    download: z.boolean().optional(),
});
//# sourceMappingURL=paypal.js.map